/**
 * Comment model interfaces matching the Comments API specification
 * 
 * @remarks
 * These models correspond to the Comments API endpoints at `/api/comments`
 * Supports hierarchical comments (replies), reactions, and nested reply loading.
 * 
 * @see COMMENTS_API_REFERENCE.md for complete API documentation
 */

import { ActivatableEntity, UserRef, BlogPostRef, Reaction, ReactionType } from './base';

/**
 * Supported content types for comments.
 * Replaces the legacy `blogPostId`-only model.
 */
export type CommentContentType =
  | 'blog_post'
  | 'post_series'
  | 'video_series'
  | 'audio_series'
  | 'image_gallery';

/**
 * Core Comment model matching API schema
 * 
 * @remarks
 * Represents a comment with unpopulated references.
 * For populated version, use CommentPopulated.
 */
export interface Comment extends ActivatableEntity {
  /**
   * The type of content this comment belongs to.
   */
  contentType: CommentContentType;

  /**
   * MongoDB ObjectId of the content item this comment belongs to.
   */
  contentId: string;

  /**
   * @deprecated Use `contentId` with `contentType: 'blog_post'` instead.
   * Kept for backward compatibility — populated only for blog post comments.
   */
  blogPostId?: string;

  /**
   * Author user ID
   */
  authorId: string;

  /**
   * Comment text (1-1000 characters)
   */
  comment: string;

  /**
   * Parent comment ID for replies
   * null for top-level comments
   */
  parentCommentId: string | null;

  /**
   * Array of reactions to this comment
   */
  reactions: Reaction[];
}

/**
 * Comment with populated references
 * 
 * @remarks
 * Returned by most Comment API endpoints.
 * Includes populated blog post and author details.
 */
export interface CommentPopulated extends Omit<Comment, 'blogPostId' | 'authorId'> {
  /**
   * @deprecated Use `contentId` with `contentType: 'blog_post'` instead.
   * Null for non-blog-post content types.
   */
  blogPostId?: BlogPostRef | null;

  /**
   * Populated author reference
   * Null when the author account has been deleted.
   */
  authorId: UserRef | null;

  /**
   * Nested replies (when includeReplies is true)
   * Only present in list responses
   */
  replies?: CommentPopulated[];

  /**
   * Total count of replies
   * Only present in list responses
   */
  replyCount?: number;

  /**
   * Whether there are more replies beyond what's shown
   * Only present in list responses
   */
  hasMoreReplies?: boolean;

  /**
   * Number of replies shown in the replies array
   * Only present in list responses
   */
  repliesShown?: number;
}

/**
 * Data Transfer Object for creating a new comment
 * 
 * @remarks
 * Used in POST /api/comments
 */
export interface CreateCommentDto {
  /**
   * The type of content being commented on.
   */
  contentType: CommentContentType;

  /**
   * MongoDB ObjectId of the content item being commented on.
   */
  contentId: string;

  /**
   * Comment text (required, 1-1000 characters)
   */
  comment: string;

  /**
   * Parent comment ID for replies (optional, MongoDB ObjectId)
   * Omit for top-level comments
   */
  parentCommentId?: string;

  /**
   * @deprecated Use `contentType: 'blog_post'` + `contentId` instead.
   * Accepted server-side for backward compatibility only.
   */
  blogPostId?: string;
}

/**
 * Data Transfer Object for updating a comment
 * 
 * @remarks
 * Used in PUT /api/comments/:id
 * Only the comment text can be updated
 */
export interface UpdateCommentDto {
  /**
   * Updated comment text (required, 1-1000 characters)
   */
  comment: string;
}

/**
 * Data Transfer Object for adding a reaction
 * 
 * @remarks
 * Used in POST /api/comments/:id/reactions
 */
export interface AddReactionDto {
  /**
   * Reaction type (required)
   */
  type: ReactionType;
}

/**
 * Query parameters for getting comments
 * 
 * @remarks
 * Used in GET /api/comments
 */
export interface GetCommentsQueryParams {
  /**
   * The type of content to get comments for.
   */
  contentType: CommentContentType;

  /**
   * MongoDB ObjectId of the content item to get comments for.
   */
  contentId: string;

  /**
   * @deprecated Use `contentType: 'blog_post'` + `contentId` instead.
   * Accepted server-side for backward compatibility only.
   */
  blogPostId?: string;

  /**
   * Parent comment ID to get replies for
   * Use 'null' string for top-level comments
   * Omit to get all comments
   */
  parentCommentId?: string | 'null';

  /**
   * Whether to include nested replies
   * @default true
   */
  includeReplies?: boolean;

  /**
   * Maximum number of replies to include per comment
   * @default 5
   * @max 50
   */
  repliesLimit?: number;

  /**
   * Page number for pagination
   * @default 1
   */
  page?: number;

  /**
   * Items per page
   * @default 20
   * @max 50
   */
  limit?: number;
}

/**
 * API response wrapper for single comment
 */
export interface CommentResponse {
  success: boolean;
  data: CommentPopulated;
  message?: string;
}

/**
 * API response wrapper for comment list
 */
export interface CommentsListResponse {
  success: boolean;
  count: number;
  data: CommentPopulated[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    pages: number;
  };
  meta?: {
    includeReplies: boolean;
    repliesLimit: number;
  };
  message?: string;
}

/**
 * API response wrapper for comment creation
 */
export interface CreateCommentResponse {
  success: boolean;
  data: CommentPopulated;
  message: 'Comment created successfully';
}

/**
 * API response wrapper for comment update
 */
export interface UpdateCommentResponse {
  success: boolean;
  data: CommentPopulated;
  message: 'Comment updated successfully';
}

/**
 * API response wrapper for comment deletion
 */
export interface DeleteCommentResponse {
  success: boolean;
  message: 'Comment deleted successfully';
}

/**
 * API response wrapper for adding reaction
 */
export interface AddReactionResponse {
  success: boolean;
  data: CommentPopulated;
  message: 'Reaction added successfully';
}

/**
 * API response wrapper for removing reaction
 */
export interface RemoveReactionResponse {
  success: boolean;
  data: CommentPopulated;
  message: 'Reaction removed successfully';
}
